import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { aiModeLabel, aiProviderLabel, branchScopedLocks, buildRichPlanSections, collaborationColor, emptyAnalysisDraft, emptyCollaborationState, eventTargetsBranch, extractOutlinePreview, formatTargetPages, getCollaborationDisplayName, getCollaborationSessionId, getStoredActiveBranchId, hasGeneratedReport, imageProcessingKey, isOutlineInstruction, jobActivityDetail, mergeDriveFilePage, mergeJobTrace, nextDriveBreadcrumbs, normalizeAiMode, normalizeAiModes, normalizeAiProviders, normalizeBranchId, normalizeOutlinePreview, parseTargetPages, pendingClarificationQuestions, reportAssistantActionInstructions, reportAssistantActionKey, reportAssistantChangeTargets, sourcePathLabel, sourceStatusLabel, storeActiveBranchId, toAnalysisDraft, uploadSourceBatch, visualAssetSourceKey, visualReviewIsCurrent } from "./GuttenbergPageUtils";
import { AiActivityPanel, BranchPanel, CollaborationStatus, CostPanel, DriveSourcePicker, ImageCropModal, PlanReviewPanel, ReadOnlyReportPage, WorkflowStepper } from "./GuttenbergWorkflowPanels";
import { AnalysisPanel, ClarificationQuestionModal, ImageBankPanel, ReportContextPanel } from "./GuttenbergAnalysisPanels";
import { CommentSuggestionDiff, FullReportPreview, OutlineLivePreview, ReportAttributionToggle, ReportOutlinePanel, UserInputQueue, VersionPanel } from "./GuttenbergReportPreview";
import { AUTO_RETOUCH_INSTRUCTION, chartCommentTitle, contextImageSrc, defaultImageCaption, pathCaptionFallback, reportImageSrc } from "./GuttenbergImageAndDiff";
import { changeTypeLabel, chartCommentTargetText, chartTitle, cssEscape, insertMarkdownBlockAfterIndex, replaceMarkdownBlockByIndex } from "./GuttenbergChartMarkdown";
import { useDeferredRetry, useQueuedProjectLoad } from "./GuttenbergProjectStateHooks";

export function useGuttenbergProjectCore(projectId) {
    const [project, setProject] = useState(null);

    const [report, setReport] = useState(null);

    // `null` is a valid, fully-loaded answer when a project has no report yet.
    // Keep transport readiness separate so analysis cannot interpret the
    // initial null as "report absent" and auto-regenerate an outline while the
    // real report is still travelling over the network.
    const [reportLoaded, setReportLoaded] = useState(false);

    const [chatMessage, setChatMessage] = useState("");

    const [titleDraft, setTitleDraft] = useState("");

    const [contextDraft, setContextDraft] = useState("");

    const [contextPanelOpen, setContextPanelOpen] = useState(false);

    const [contextSaving, setContextSaving] = useState(false);

    const [contextUploadBusy, setContextUploadBusy] = useState(false);

    const [targetPages, setTargetPages] = useState("");

    const [status, setStatus] = useState("");

    const [busy, setBusy] = useState(false);

    const [exportRunning, setExportRunning] = useState(false);

    const [attributionSaving, setAttributionSaving] = useState(false);

    const [job, setJob] = useState(null);

    const [jobTrace, setJobTrace] = useState([]);

    const [activityClock, setActivityClock] = useState(() => Date.now());

    const [liveSections, setLiveSections] = useState({});

    const [intakeEvents, setIntakeEvents] = useState([]);

    const [analysisRunning, setAnalysisRunning] = useState(false);

    const [commentMode, setCommentMode] = useState(false);

    const [pendingComment, setPendingComment] = useState(null);

    const [commentAnchor, setCommentAnchor] = useState(null);

    const [commentDraft, setCommentDraft] = useState("");

    const [manualImageFile, setManualImageFile] = useState(null);

    const [manualImageCaption, setManualImageCaption] = useState("");

    const [manualImageNote, setManualImageNote] = useState("");

    const [commentReplies, setCommentReplies] = useState({});

    const [reportInstruction, setReportInstruction] = useState("");

    const [reportInstructionImages, setReportInstructionImages] = useState([]);

    const [reportInstructionFiles, setReportInstructionFiles] = useState([]);

    const [reportInstructionBankImages, setReportInstructionBankImages] = useState([]);

    const [selectedBankImage, setSelectedBankImage] = useState(null);

    const [outlineDraft, setOutlineDraft] = useState("");

    const [outlineInstruction, setOutlineInstruction] = useState("");

    const [outlineRunning, setOutlineRunning] = useState(false);

    const [outlineApprovalPending, setOutlineApprovalPending] = useState(false);

    const [outlinePreview, setOutlinePreview] = useState([]);

    const [reportLivePhase, setReportLivePhase] = useState("idle");

    const reportLivePhaseRef = useRef("idle");

    const operationStartingRef = useRef(false);

    const contextAutoIntegrationRef = useRef(false);

    const [mainTab, setMainTab] = useState("analysis");

    const initialTabResolvedRef = useRef(false);

    const [initialTabResolved, setInitialTabResolved] = useState(false);

    const [analysis, setAnalysis] = useState(null);

    const [costs, setCosts] = useState(null);

    const [driveFiles, setDriveFiles] = useState(null);

    const [driveSelection, setDriveSelection] = useState({});

    const [driveLoading, setDriveLoading] = useState(false);

    const [driveFolderId, setDriveFolderId] = useState("");

    const [driveBreadcrumbs, setDriveBreadcrumbs] = useState([{ id: "", name: "Drive" }]);

    const [driveQuery, setDriveQuery] = useState("");

    const driveRequestSequenceRef = useRef(0);

    const { timerRef: stateReloadTimerRef, clearRetry: clearStateReload, scheduleRetry: scheduleStateReload } = useDeferredRetry();

    const driveLocationRef = useRef({ folderId: "", query: "" });

    const notifiedQuestionIdsRef = useRef(new Set());

    const [browserNotificationPermission, setBrowserNotificationPermission] = useState(() => {
            if (typeof window === "undefined" || !("Notification" in window))
                return "unsupported";
            return window.Notification.permission;
        });

    const [analysisDraft, setAnalysisDraft] = useState(emptyAnalysisDraft());

    const analysisDraftDirtyRef = useRef(false);

    const briefDirtyRef = useRef(false);

    const [processingCommentIds, setProcessingCommentIds] = useState({});

    const [processingSectionIds, setProcessingSectionIds] = useState({});

    const [editMode, setEditMode] = useState(false);

    const [savingSectionIds, setSavingSectionIds] = useState({});

    const [processingImageKeys, setProcessingImageKeys] = useState({});

    const imageActionByCommentRef = useRef({});

    const [activeVersion, setActiveVersion] = useState(null);

    const [activeAssistantReview, setActiveAssistantReview] = useState(null);

    const [milestoneLabel, setMilestoneLabel] = useState("");

    const [workflowCollapsed, setWorkflowCollapsed] = useState(false);

    const [aiMode, setAiMode] = useState("standard");

    const [fastMode, setFastMode] = useState(false);

    const [fastModeInfo, setFastModeInfo] = useState(null);

    const [fastModeBusy, setFastModeBusy] = useState(false);

    const [aiProvider, setAiProvider] = useState("openai");

    const [aiProviderInfo, setAiProviderInfo] = useState(null);

    const [aiProviderBusy, setAiProviderBusy] = useState(false);

    const [clarificationQuestions, setClarificationQuestions] = useState([]);

    const [activeClarificationQuestion, setActiveClarificationQuestion] = useState(null);

    const [clarificationAnswerBusy, setClarificationAnswerBusy] = useState(false);

    const clarificationAnswerInFlightRef = useRef(false);

    const [imageEditTarget, setImageEditTarget] = useState(null);

    const collaborationSessionRef = useRef(getCollaborationSessionId());

    const collaborationContextRef = useRef({});

    const editingTargetRef = useRef(null);

    const [collaboration, setCollaboration] = useState(emptyCollaborationState());

    const [branchState, setBranchState] = useState(null);

    const initialBranchId = getStoredActiveBranchId(projectId);

    const [activeBranchId, setActiveBranchId] = useState(initialBranchId);

    const activeBranchIdRef = useRef(initialBranchId);

    const [branchName, setBranchName] = useState("");

    const [mergeFeedback, setMergeFeedback] = useState(null);

    const [reportOutlineCollapsed, setReportOutlineCollapsed] = useState(false);

    const [activeReportTool, setActiveReportTool] = useState("assistant");

    const [reportAssistantThinking, setReportAssistantThinking] = useState(false);

    const [appliedReportAssistantActions, setAppliedReportAssistantActions] = useState({});

    const [activeReportAssistantThreadId, setActiveReportAssistantThreadId] = useState("");

    const applyRuntimeSettings = useCallback((info) => {
            const mode = normalizeAiMode(info?.ai_mode || info?.mode, Boolean(info?.fast_mode));
            setAiMode(mode);
            setFastMode(mode === "fast");
            setFastModeInfo(info);
            setAiProvider(info?.provider || "openai");
            setAiProviderInfo(info);
        }, []);

    function setActiveBranchForSession(branchId) {
            const nextBranchId = branchId || "main";
            activeBranchIdRef.current = nextBranchId;
            setActiveBranchId(nextBranchId);
            storeActiveBranchId(projectId, nextBranchId);
        }

    function applyAnalysisUpdate(nextAnalysis, options = {}) {
            setAnalysis(nextAnalysis);
            const pendingQuestions = visualReviewIsCurrent(nextAnalysis)
                ? pendingClarificationQuestions(nextAnalysis)
                : [];
            setClarificationQuestions(pendingQuestions);
            setActiveClarificationQuestion((current) => {
                const authoritativeCurrent = current
                    ? pendingQuestions.find((question) => question.id === current.id)
                    : null;
                if (authoritativeCurrent)
                    return authoritativeCurrent;
                return pendingQuestions[0] ?? null;
            });
            if (nextAnalysis) {
                if (options.forceDraft || !analysisDraftDirtyRef.current) {
                    setAnalysisDraft(toAnalysisDraft(nextAnalysis));
                }
                return;
            }
            if (options.forceDraft || !analysisDraftDirtyRef.current) {
                setAnalysisDraft(emptyAnalysisDraft());
            }
        }

    function patchVisualAsset(assetId, patch) {
            setAnalysis((current) => current ? {
                ...current,
                visual_assets: (current.visual_assets ?? []).map((asset) => asset.id === assetId
                    ? { ...asset, ...patch }
                    : asset)
            } : current);
        }

    function patchVisualAssets(assetIds, patch) {
            const ids = assetIds instanceof Set ? assetIds : new Set(assetIds);
            setAnalysis((current) => current ? {
                ...current,
                visual_assets: (current.visual_assets ?? []).map((asset) => ids.has(asset.id)
                    ? { ...asset, ...patch }
                    : asset)
            } : current);
        }

    function applyLightProjectState(projectState) {
            if (!projectState)
                return;
            setProject((current) => current ? { ...current, ...projectState } : current);
        }

    function applyLightAnalysisState(analysisState) {
            if (!analysisState)
                return;
            setAnalysis((current) => current ? { ...current, ...analysisState } : current);
        }

    function updateAnalysisDraft(action) {
            analysisDraftDirtyRef.current = true;
            setAnalysisDraft(action);
        }

    function updateContextDraft(value) {
            briefDirtyRef.current = true;
            setContextDraft(value);
        }

    function updateTitleDraft(value) {
            briefDirtyRef.current = true;
            setTitleDraft(value);
        }

    function updateTargetPagesDraft(value) {
            briefDirtyRef.current = true;
            setTargetPages(value);
        }

    function resolveInitialTab(loadedReport) {
            if (initialTabResolvedRef.current)
                return;
            initialTabResolvedRef.current = true;
            setMainTab(hasGeneratedReport(loadedReport) ? "report" : "analysis");
            setInitialTabResolved(true);
        }

    useEffect(() => {
            let cancelled = false;
            api.getFastMode()
                .then((info) => {
                if (cancelled)
                    return;
                applyRuntimeSettings(info);
            })
                .catch(() => { });
            return () => {
                cancelled = true;
            };
        }, [applyRuntimeSettings]);

    async function changeAiMode(mode) {
            if (fastModeBusy)
                return;
            const next = normalizeAiMode(mode);
            setFastModeBusy(true);
            try {
                const info = await api.setAiMode(next);
                applyRuntimeSettings(info);
                const providerOption = normalizeAiProviders(info).find((item) => item.id === (info?.provider || aiProvider));
                const fastModel = providerOption?.fast_model || info?.flash_model || "gpt-4.1-mini";
                if (next === "fast")
                    setStatus(`Mode IA Fast actif (${fastModel}) - prochains appels IA forces sur les modeles rapides`);
                else if (next === "smart")
                    setStatus("Mode IA Smart actif - rapide sur les taches simples, intelligent sur les decisions");
                else if (next === "agents")
                    setStatus(`Mode Agents beta actif - superviseur habituel, investigations deleguees a ${info?.investigation_model || "GPT-5.6 Luna"}`);
                else
                    setStatus("Mode IA Standard actif - retour au routage habituel des modeles");
            }
            catch (error) {
                setStatus(error.message || "Impossible de changer le mode IA");
            }
            finally {
                setFastModeBusy(false);
            }
        }

    async function changeAiProvider(provider) {
            if (aiProviderBusy)
                return;
            setAiProviderBusy(true);
            try {
                const info = await api.setAiProvider(provider);
                applyRuntimeSettings(info);
                const option = normalizeAiProviders(info).find((item) => item.id === provider);
                const mode = normalizeAiMode(info?.ai_mode || info?.mode, Boolean(info?.fast_mode));
                const model = mode === "fast" && option?.fast_model ? option.fast_model : option?.model;
                const modelLabel = model ? ` (${model})` : "";
                if (provider === "hybrid")
                    setStatus("Mode hybride actif - OpenAI pilote; Claude redige les sections finales");
                else
                    setStatus(`Provider IA Guttenberg: ${option?.label || aiProviderLabel(provider)}${modelLabel}`);
            }
            catch (error) {
                setStatus(error.message || "Impossible de changer le provider IA");
            }
            finally {
                setAiProviderBusy(false);
            }
        }

    function applyProjectUpdate(nextProject, options = {}) {
            setProject(nextProject);
            if (options.forceBrief || !briefDirtyRef.current) {
                setTitleDraft(nextProject.title ?? "");
                setContextDraft(nextProject.user_context ?? "");
                setTargetPages(formatTargetPages(nextProject.target_pages));
            }
        }

    async function loadOnce(branchOverride = activeBranchIdRef.current) {
            const selectedBranchId = branchOverride || "main";
            // Les requetes ci-dessous sont independantes (hors dependances internes report<-branches,
            // drive<-project) : on les lance en parallele pour que le rechargement post-action reste reactif.
            const loadProjectAndDrive = async () => {
                try {
                    const loadedProject = await api.getProject(projectId);
                    applyProjectUpdate(loadedProject);
                    if (loadedProject.deepflow_project_id) {
                        const requestId = ++driveRequestSequenceRef.current;
                        const location = { ...driveLocationRef.current };
                        setDriveLoading(true);
                        try {
                            const listing = await api.driveFiles(projectId, {
                                folderId: location.query ? undefined : location.folderId,
                                query: location.query || undefined
                            });
                            if (requestId === driveRequestSequenceRef.current)
                                setDriveFiles(listing);
                        }
                        catch (error) {
                            if (requestId === driveRequestSequenceRef.current)
                                setDriveFiles({ status: "non_lu", reason: error.message || "Drive non disponible.", files: [] });
                        }
                        finally {
                            if (requestId === driveRequestSequenceRef.current)
                                setDriveLoading(false);
                        }
                    }
                    else {
                        driveRequestSequenceRef.current += 1;
                        setDriveLoading(false);
                        setDriveFiles(null);
                    }
                }
                catch (error) {
                    setStatus(error instanceof Error ? error.message : "Erreur");
                }
            };
            const loadBranchAndReport = async () => {
                setReportLoaded(false);
                let branchIdForReport = selectedBranchId;
                const [branchResult, reportResult] = await Promise.allSettled([
                    api.branches(projectId, branchIdForReport),
                    api.report(projectId, branchIdForReport),
                ]);
                if (branchResult.status === "fulfilled") {
                    const loadedBranchState = branchResult.value;
                    const branchExists = loadedBranchState?.branches?.some((branch) => branch.id === branchIdForReport);
                    if (!branchExists) {
                        branchIdForReport = "main";
                        setActiveBranchForSession("main");
                    }
                    setBranchState({ ...loadedBranchState, active_branch_id: branchIdForReport });
                }
                else {
                    setBranchState(null);
                }
                let loadedReport = reportResult.status === "fulfilled" ? reportResult.value : null;
                let reportRequestCompleted = reportResult.status === "fulfilled" || reportResult.reason?.status === 404;
                if (branchIdForReport !== selectedBranchId) {
                    try {
                        loadedReport = await api.report(projectId, branchIdForReport);
                        reportRequestCompleted = true;
                    }
                    catch (error) {
                        reportRequestCompleted = error?.status === 404;
                        if (reportRequestCompleted)
                            loadedReport = null;
                    }
                }
                if (!reportRequestCompleted)
                    return;
                if (loadedReport) {
                    setReport(loadedReport);
                    resolveInitialTab(loadedReport);
                }
                else {
                    setReport(null);
                    resolveInitialTab(null);
                }
                setReportLoaded(true);
            };
            const loadAnalysis = async () => {
                let lastError = null;
                for (let attempt = 0; attempt < 2; attempt += 1) {
                    try {
                        const nextAnalysis = await api.analysis(projectId);
                        clearStateReload();
                        applyAnalysisUpdate(nextAnalysis);
                        return;
                    }
                    catch (error) {
                        lastError = error;
                        if (![502, 503, 504].includes(error?.status) || attempt === 1)
                            break;
                        await new Promise((resolve) => window.setTimeout(resolve, 1200));
                    }
                }
                // A transient proxy timeout must never erase the last valid
                // questions/images from the UI. Keep them and retry once the
                // worker has released the workspace.
                if ([502, 503, 504].includes(lastError?.status) && scheduleStateReload(
                    () => load(activeBranchIdRef.current),
                    2500,
                )) {
                    setStatus("Etat d'analyse momentanement indisponible; nouvelle tentative automatique...");
                }
                else if (lastError?.status !== 404) {
                    setStatus(lastError?.message || "Impossible de synchroniser l'analyse pour le moment.");
                }
            };
            const loadCosts = async () => {
                try {
                    setCosts(await api.costs(projectId));
                }
                catch {
                    setCosts(null);
                }
            };
            const loadCollaboration = async () => {
                try {
                    setCollaboration(await api.collaboration(projectId));
                }
                catch {
                    setCollaboration(emptyCollaborationState());
                }
            };
            await Promise.all([
                loadProjectAndDrive(),
                loadBranchAndReport(),
                loadAnalysis(),
                loadCosts(),
                loadCollaboration()
            ]);
        }

    const load = useQueuedProjectLoad(loadOnce);

    async function refreshReportAfterImageChange(sectionId, updatedManifest = null, branchOverride = activeBranchIdRef.current) {
            if (updatedManifest) {
                setReport((current) => current ? { ...current, manifest: updatedManifest } : current);
            }
            const loadedReport = await api.report(projectId, branchOverride || "main");
            setReport(loadedReport);
            resolveInitialTab(loadedReport);
            setLiveSections((current) => {
                const next = { ...current };
                delete next[sectionId];
                return next;
            });
            api.analysis(projectId)
                .then((nextAnalysis) => applyAnalysisUpdate(nextAnalysis))
                .catch(() => { });
            return loadedReport;
        }

    async function run(label, action, options = {}) {
            setBusy(true);
            setStatus(label);
            try {
                await action();
                if (!options.skipReload)
                    await load();
                if (!options.preserveStatus)
                    setStatus("Termine");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setBusy(false);
            }
        }

    function clearLiveSectionsForJob(nextJob) {
            if (!nextJob || nextJob.status !== "cancelled")
                return;
            if (["generate-report", "report-instruction", "apply-comments"].includes(nextJob.type)) {
                setLiveSections({});
            }
        }

    function setImageProcessingKey(key, active) {
            if (!key)
                return;
            setProcessingImageKeys((current) => {
                if (active)
                    return current[key] ? current : { ...current, [key]: true };
                if (!current[key])
                    return current;
                const next = { ...current };
                delete next[key];
                return next;
            });
        }

    function setSectionSaving(sectionId, active) {
            if (!sectionId)
                return;
            setSavingSectionIds((current) => {
                if (active)
                    return current[sectionId] ? current : { ...current, [sectionId]: true };
                if (!current[sectionId])
                    return current;
                const next = { ...current };
                delete next[sectionId];
                return next;
            });
        }

    function rememberImageComment(commentId, sectionId, imagePath) {
            const key = imageProcessingKey(sectionId, imagePath);
            if (!commentId || !key)
                return;
            imageActionByCommentRef.current[commentId] = key;
            setImageProcessingKey(key, true);
        }

    function clearImageProcessingForComment(commentId, fallbackSectionId = "", fallbackImagePath = "") {
            const rememberedKey = commentId ? imageActionByCommentRef.current[commentId] : "";
            const key = rememberedKey || imageProcessingKey(fallbackSectionId, fallbackImagePath);
            if (commentId)
                delete imageActionByCommentRef.current[commentId];
            setImageProcessingKey(key, false);
        }

    async function uploadFiles(event) {
            const files = Array.from(event.target.files ?? []);
            if (!files.length)
                return;
            await run("Upload...", async () => {
                // Uploading sources is intentionally inert. syncBrief() persists
                // context through an endpoint that may launch its integration and
                // ultimately resume analysis. A file drop must never have that
                // hidden side effect; the dirty brief remains preserved locally
                // by briefDirtyRef until the user saves or starts analysis.
                await uploadSourceBatch(projectId, files, api.uploadFile);
            });
            event.target.value = "";
            setStatus("Fichiers ajoutes. L'analyse attend ton lancement explicite.");
        }

    async function uploadClientLogo(event) {
            const file = event.target.files?.[0] ?? null;
            if (!file)
                return;
            await run("Ajout du logo client...", async () => {
                await api.uploadClientLogo(projectId, file);
            });
            event.target.value = "";
            setStatus("Logo client ajoute.");
        }

    function resetManualImageDraft() {
            setManualImageFile(null);
            setManualImageCaption("");
            setManualImageNote("");
        }

    async function uploadContextAttachments(files, note) {
            const ids = [];
            for (const file of files) {
                const uploaded = await api.uploadContextImage(projectId, file, defaultImageCaption(file), note, activeBranchIdRef.current);
                ids.push(uploaded.attachment.id);
            }
            return ids;
        }

    async function uploadReportAssistantFiles(files) {
            const ids = [];
            for (const file of files) {
                const uploaded = await api.uploadFile(projectId, file);
                const items = Array.isArray(uploaded) ? uploaded : [uploaded];
                items.forEach((item) => {
                    if (item?.id)
                        ids.push(item.id);
                });
            }
            return ids;
        }

    async function syncBrief() {
            const updated = await api.updateContext(projectId, {
                title: titleDraft,
                user_context: contextDraft,
                target_pages: parseTargetPages(targetPages)
            });
            briefDirtyRef.current = false;
            applyProjectUpdate(updated, { forceBrief: true });
            return updated;
        }

    async function refreshDriveFiles(options = {}) {
            if (!project?.deepflow_project_id)
                return;
            const currentLocation = driveLocationRef.current;
            const nextFolderId = options.folderId !== undefined ? options.folderId : currentLocation.folderId;
            const nextQuery = options.query !== undefined ? options.query : currentLocation.query;
            const append = !!options.append;
            const requestId = ++driveRequestSequenceRef.current;
            driveLocationRef.current = { folderId: nextFolderId || "", query: nextQuery || "" };
            setDriveLoading(true);
            try {
                const next = await api.driveFiles(projectId, {
                    folderId: nextQuery ? undefined : nextFolderId,
                    query: nextQuery || undefined,
                    pageToken: options.pageToken
                });
                if (requestId !== driveRequestSequenceRef.current)
                    return;
                setDriveFiles((current) => mergeDriveFilePage(current, next, append));
            }
            catch (error) {
                if (requestId === driveRequestSequenceRef.current)
                    setDriveFiles({ status: "non_lu", reason: error.message || "Drive non disponible.", files: [] });
            }
            finally {
                if (requestId === driveRequestSequenceRef.current)
                    setDriveLoading(false);
            }
        }

    function openDriveFolder(file) {
            if (!file?.id)
                return;
            if (!driveLocationRef.current.query && driveLocationRef.current.folderId === file.id)
                return;
            const openedFromSearch = Boolean(driveLocationRef.current.query);
            driveLocationRef.current = { folderId: file.id, query: "" };
            setDriveFolderId(file.id);
            setDriveQuery("");
            setDriveBreadcrumbs((current) => openedFromSearch
                ? nextDriveBreadcrumbs([{ id: "", name: "Drive" }], file)
                : nextDriveBreadcrumbs(current, file));
            refreshDriveFiles({ folderId: file.id, query: "" });
        }

    function openDriveBreadcrumb(index) {
            const crumb = driveBreadcrumbs[index];
            if (!crumb)
                return;
            const nextBreadcrumbs = driveBreadcrumbs.slice(0, index + 1);
            driveLocationRef.current = { folderId: crumb.id, query: "" };
            setDriveBreadcrumbs(nextBreadcrumbs);
            setDriveFolderId(crumb.id);
            setDriveQuery("");
            refreshDriveFiles({ folderId: crumb.id, query: "" });
        }

    function searchDriveFiles(event) {
            event.preventDefault();
            refreshDriveFiles({ query: driveQuery.trim() });
        }

    function clearDriveSearch() {
            setDriveQuery("");
            refreshDriveFiles({ folderId: driveFolderId, query: "" });
        }

    async function enableBrowserQuestionNotifications() {
            if (typeof window === "undefined" || !("Notification" in window))
                return;
            try {
                const permission = await window.Notification.requestPermission();
                setBrowserNotificationPermission(permission);
            }
            catch {
                setBrowserNotificationPermission("denied");
            }
        }

    function toggleDriveFile(file) {
            if (!file?.id)
                return;
            const kind = file.is_folder ? "folder" : "file";
            setDriveSelection((current) => {
                const next = { ...current };
                if (next[file.id])
                    delete next[file.id];
                else
                    next[file.id] = { kind, name: file.name || "" };
                return next;
            });
        }

    async function importSelectedDriveFiles() {
            const entries = Object.entries(driveSelection);
            const fileIds = entries
                .filter(([, item]) => item.kind === "file")
                .map(([fileId]) => fileId);
            const folderIds = entries
                .filter(([, item]) => item.kind === "folder")
                .map(([folderId]) => folderId);
            if (!fileIds.length && !folderIds.length)
                return;
            if (operationLocked()) {
                setStatus("Operation Guttenberg deja en cours.");
                return;
            }
            operationStartingRef.current = true;
            setBusy(true);
            setStatus("Import Drive...");
            try {
                const createdJob = await api.importDriveFiles(projectId, { fileIds, folderIds, recursive: true });
                applyJobUpdate(createdJob);
                setDriveSelection({});
                setStatus(createdJob.message || "Import Drive lance.");
                if (!isActiveJob(createdJob)) {
                    setBusy(false);
                    await load();
                }
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setBusy(false);
            }
            finally {
                operationStartingRef.current = false;
            }
        }

    async function sendChat(event) {
            event.preventDefault();
            if (!chatMessage.trim())
                return;
            const message = chatMessage;
            setChatMessage("");
            setBusy(true);
            setStatus("Message pris en compte...");
            try {
                await syncBrief();
                const updated = await api.chat(projectId, message);
                briefDirtyRef.current = false;
                applyProjectUpdate(updated, { forceBrief: true });
                if (report?.manifest.sections.length && isOutlineInstruction(message)) {
                    await api.refineOutline(projectId, message);
                    await load();
                    setStatus("Plan modifie. Relis puis valide le plan.");
                    return;
                }
                if (updated.source_files.length > 0) {
                    setStatus(visualReviewIsCurrent(analysis)
                        ? "Information enregistree; son integration va demarrer sans relancer toute l'analyse."
                        : "Information enregistree. Termine les images puis lance explicitement l'analyse.");
                }
                else {
                    setStatus("Ajoute les fichiers sources pour lancer l'analyse.");
                }
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setBusy(false);
            }
        }

    async function answerClarificationQuestion(question, answer, dismissed = false, attachmentFiles = [], selectedVisualAssetIds = []) {
            if (!question || clarificationAnswerInFlightRef.current || clarificationAnswerBusy || analysisRunning)
                return;
            clarificationAnswerInFlightRef.current = true;
            const previousQuestions = clarificationQuestions;
            const previousActiveQuestion = activeClarificationQuestion;
            setClarificationAnswerBusy(true);
            try {
                const sourceFileIds = [];
                for (const file of attachmentFiles) {
                    const uploaded = await api.uploadFile(projectId, file);
                    const sources = Array.isArray(uploaded) ? uploaded : [uploaded];
                    sources.forEach((source) => {
                        if (source?.id)
                            sourceFileIds.push(source.id);
                    });
                }
                const resolvedAnswer = dismissed ? (question.default_answer || answer || "Non precise") : answer;
                const optimisticQuestion = {
                    ...question,
                    answer: resolvedAnswer,
                    source_file_ids: [...new Set([...(question.source_file_ids ?? []), ...sourceFileIds])],
                    selected_visual_asset_ids: [...new Set(selectedVisualAssetIds)],
                    status: dismissed ? "dismissed" : "answered",
                };
                const remainingQuestions = previousQuestions.filter((item) => item.id !== question.id);
                setClarificationQuestions(remainingQuestions);
                setActiveClarificationQuestion(remainingQuestions[0] ?? null);
                setAnalysis((current) => current ? {
                    ...current,
                    clarification_questions: (current.clarification_questions ?? []).map((item) => item.id === question.id
                        ? optimisticQuestion
                        : item),
                } : current);
                const response = await api.answerQuestion(projectId, question.id, {
                    answer,
                    dismissed,
                    source_file_ids: sourceFileIds,
                    selected_visual_asset_ids: selectedVisualAssetIds,
                });
                if (response.project)
                    applyProjectUpdate(response.project);
                else
                    applyLightProjectState(response.project_state);
                if (response.analysis) {
                    applyAnalysisUpdate(response.analysis, { forceDraft: true });
                }
                else {
                    const savedQuestion = response.question || optimisticQuestion;
                    setAnalysis((current) => current ? {
                        ...current,
                        updated_at: response.analysis_updated_at || current.updated_at,
                        clarification_questions: (current.clarification_questions ?? []).map((item) => item.id === question.id
                            ? { ...item, ...savedQuestion }
                            : item),
                    } : current);
                    if (Array.isArray(response.pending_questions)) {
                        setClarificationQuestions(response.pending_questions);
                        setActiveClarificationQuestion((current) => {
                            const authoritativeCurrent = current
                                ? response.pending_questions.find((item) => item.id === current.id)
                                : null;
                            return authoritativeCurrent ?? response.pending_questions[0] ?? null;
                        });
                    }
                }
                if (response.patch_job)
                    applyJobUpdate(response.patch_job);
                if (response.context_job)
                    applyJobUpdate(response.context_job);
                if (response.analysis_job) {
                    applyJobUpdate(response.analysis_job);
                    setAnalysisRunning(true);
                    setBusy(true);
                }
                setStatus(response.analysis_waits_for_user_start
                    ? "Reponse enregistree. Termine les images puis lance explicitement l'analyse."
                    : response.context_job
                    ? "Reponse et pieces jointes enregistrees; integration ciblee en cours."
                    : response.context_integration_queued
                        ? "Reponse et pieces jointes enregistrees; integration apres l'operation en cours."
                    : response.analysis_job
                    ? "Reponse enregistree; reprise de l'analyse en cours."
                    : response.analysis_resume_pending
                        ? "Reponse enregistree; termine les autres precisions avant la reprise de l'analyse."
                        : dismissed
                    ? "Question marquee comme non precisee."
                    : response.patch_job
                        ? "Reponse enregistree; mise a jour ciblee de la section en cours."
                        : "Reponse enregistree pour l'analyse.");
            }
            catch (error) {
                // A question can become stale when another tab or a new analysis
                // replaces the clarification batch. Reload the authoritative state
                // instead of restoring the rejected question and trapping the user
                // in an endless retry loop. Keep the optimistic rollback only when
                // the resynchronisation itself is unavailable.
                try {
                    const [latestProject, latestAnalysis] = await Promise.all([
                        api.getProject(projectId),
                        api.analysis(projectId),
                    ]);
                    applyProjectUpdate(latestProject);
                    applyAnalysisUpdate(latestAnalysis);
                }
                catch (_reloadError) {
                    setClarificationQuestions(previousQuestions);
                    setActiveClarificationQuestion(previousActiveQuestion);
                    setAnalysis((current) => current ? {
                        ...current,
                        clarification_questions: (current.clarification_questions ?? []).map((item) => item.id === question.id
                            ? question
                            : item),
                    } : current);
                }
                setStatus(error instanceof Error ? error.message : "Impossible d'enregistrer la reponse.");
            }
            finally {
                clarificationAnswerInFlightRef.current = false;
                setClarificationAnswerBusy(false);
            }
        }

    async function startGeneration() {
            if (operationLocked()) {
                setStatus("Operation Guttenberg deja en cours.");
                return;
            }
            operationStartingRef.current = true;
            setBusy(true);
            setMainTab("report");
            setLiveSections({});
            setOutlineDraft("");
            setOutlinePreview([]);
            setReportLivePhase("writing");
            setStatus("Préparation de la rédaction...");
            try {
                await syncBrief();
                const createdJob = await api.generate(projectId);
                applyJobUpdate(createdJob);
            }
            catch (error) {
                setReportLivePhase("idle");
                setStatus(error instanceof Error ? error.message : "Erreur");
                setBusy(false);
            }
            finally {
                operationStartingRef.current = false;
            }
        }

    async function analyzeProject(syncBefore = true, label = "Analyse du dossier...") {
            if (operationLocked()) {
                setStatus("Analyse Guttenberg deja en cours.");
                return;
            }
            // The analysis click is a user gesture, so it is the right moment
            // to request browser permission once. Later questions and stage
            // transitions can then notify the user while this tab is away.
            if (browserNotificationPermission === "default")
                void enableBrowserQuestionNotifications();
            let createdJob = null;
            operationStartingRef.current = true;
            setBusy(true);
            setAnalysisRunning(true);
            setMainTab("analysis");
            setIntakeEvents([]);
            setStatus(label);
            try {
                if (syncBefore) {
                    await syncBrief();
                }
                createdJob = await api.prepare(projectId);
                applyJobUpdate(createdJob);
                setStatus(createdJob.message || "Analyse du dossier lancee.");
                if (!isActiveJob(createdJob)) {
                    await load();
                }
                return createdJob;
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setAnalysisRunning(false);
                setBusy(false);
                return null;
            }
            finally {
                operationStartingRef.current = false;
                if (!isActiveJob(createdJob)) {
                    setAnalysisRunning(false);
                    setBusy(false);
                }
            }
        }

    async function resetAnalysis() {
            if (operationLocked()) {
                setStatus("Impossible de réinitialiser pendant une opération Guttenberg.");
                return false;
            }
            const confirmed = window.confirm(
                "Réinitialiser les résultats de l'analyse ?\n\nLe brief, les documents, les images extraites et tes choix d'images seront conservés. Les constats, calculs, questions et checkpoints d'analyse seront archivés puis effacés."
            );
            if (!confirmed)
                return false;
            operationStartingRef.current = true;
            setBusy(true);
            setStatus("Réinitialisation de l'analyse...");
            try {
                const payload = await api.resetAnalysis(projectId);
                if (payload?.project)
                    setProject(payload.project);
                applyAnalysisUpdate(payload?.analysis ?? null, { forceDraft: true });
                setIntakeEvents([]);
                setOutlineDraft("");
                setOutlinePreview([]);
                setStatus("Analyse réinitialisée. Brief, sources et images conservés.");
                return true;
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur de réinitialisation");
                return false;
            }
            finally {
                operationStartingRef.current = false;
                setBusy(false);
            }
        }

    async function prepareVisuals(syncBefore = true) {
            if (operationLocked()) {
                setStatus("Operation Guttenberg deja en cours.");
                return;
            }
            let createdJob = null;
            operationStartingRef.current = true;
            setBusy(true);
            setMainTab("analysis");
            setIntakeEvents([]);
            setStatus("Extraction des sources et des images...");
            try {
                if (syncBefore) {
                    await syncBrief();
                }
                createdJob = await api.prepareVisuals(projectId);
                applyJobUpdate(createdJob);
                setStatus(createdJob.message || "Extraction des images lancee.");
                if (!isActiveJob(createdJob)) {
                    await load();
                }
                return createdJob;
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setBusy(false);
                return null;
            }
            finally {
                operationStartingRef.current = false;
                if (!isActiveJob(createdJob)) {
                    setBusy(false);
                }
            }
        }

    async function generateOutline() {
            if (operationLocked()) {
                setStatus("Operation Guttenberg deja en cours.");
                return;
            }
            let createdJob = null;
            operationStartingRef.current = true;
            setBusy(true);
            setOutlineRunning(true);
            setMainTab("report");
            setReportLivePhase("planning");
            setOutlineDraft("");
            setOutlinePreview([]);
            setStatus("Generation du plan...");
            try {
                await syncBrief();
                createdJob = await api.outline(projectId);
                applyJobUpdate(createdJob);
                setStatus(createdJob.message || "Generation du plan lancee.");
                if (!isActiveJob(createdJob)) {
                    await load();
                }
                return createdJob;
            }
            catch (error) {
                setReportLivePhase("idle");
                setStatus(error instanceof Error ? error.message : "Erreur");
                setOutlineRunning(false);
                setBusy(false);
                return null;
            }
            finally {
                operationStartingRef.current = false;
                if (!isActiveJob(createdJob)) {
                    setOutlineRunning(false);
                    setBusy(false);
                }
            }
        }

    async function approveOutline() {
            if (outlineApprovalPending)
                return;
            setOutlineApprovalPending(true);
            setBusy(true);
            setStatus("Validation du plan...");
            try {
                const saved = await api.approveOutline(projectId, true);
                setAnalysis(saved);
                setAnalysisDraft(toAnalysisDraft(saved));
                setReport((current) => current ? {
                    ...current,
                    manifest: { ...current.manifest, status: "outline_approved" }
                } : current);
                setStatus("Plan valide. La redaction peut etre lancee.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur lors de la validation du plan.");
            }
            finally {
                setOutlineApprovalPending(false);
                setBusy(false);
            }
        }

    async function refineOutline() {
            if (!outlineInstruction.trim())
                return;
            setBusy(true);
            setOutlineRunning(true);
            setReportLivePhase("planning");
            setStatus("Modification du plan...");
            try {
                await api.refineOutline(projectId, outlineInstruction);
                setOutlineInstruction("");
                await load();
                setReportLivePhase("idle");
                setStatus("Plan modifie. Relis puis valide le plan.");
            }
            catch (error) {
                setReportLivePhase("idle");
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setOutlineRunning(false);
                setBusy(false);
            }
        }

    async function submitOutlineInstruction(event) {
            event.preventDefault();
            await refineOutline();
        }

    async function runFinalReview() {
            await run("Relecture agentique...", async () => {
                await api.runReview(projectId);
            });
        }

    async function saveContextPanel() {
            if (contextSaving)
                return;
            contextAutoIntegrationRef.current = true;
            setContextSaving(true);
            try {
                const updated = await syncBrief();
                const response = await api.integrateContext(projectId);
                applyProjectUpdate(response.project || updated, { forceBrief: true });
                if (response.job)
                    applyJobUpdate(response.job);
                setStatus(response.job
                    ? "Nouvelles informations en cours d'integration."
                    : response.queued
                        ? visualReviewIsCurrent(analysis)
                            ? "Informations enregistrees; integration apres l'operation en cours."
                            : "Informations enregistrees. Termine les images puis lance explicitement l'analyse."
                        : "Informations enregistrees.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Impossible d'enregistrer les informations.");
            }
            finally {
                contextAutoIntegrationRef.current = false;
                setContextSaving(false);
            }
        }

    async function uploadLiveContextFiles(event) {
            const files = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (!files.length || contextUploadBusy)
                return;
            setContextUploadBusy(true);
            try {
                const response = await api.uploadContextFiles(projectId, files);
                setProject((current) => current ? {
                    ...current,
                    source_files: [
                        ...(current.source_files ?? []),
                        ...(response.sources ?? []).filter((source) => !(current.source_files ?? []).some((item) => item.id === source.id)),
                    ],
                    context_integration_status: response.queued ? "pending" : "running",
                } : current);
                if (response.job)
                    applyJobUpdate(response.job);
                setStatus(response.job
                    ? `${files.length} fichier(s) ajoute(s); integration incrementale en cours.`
                    : `${files.length} fichier(s) ajoute(s); integration apres l'operation en cours.`);
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Impossible d'ajouter les fichiers.");
            }
            finally {
                setContextUploadBusy(false);
            }
        }

    async function toggleGuttenbergAttribution() {
            if (!report?.manifest || attributionSaving)
                return;
            const visible = report.manifest.show_guttenberg_attribution !== false;
            const nextVisible = !visible;
            setAttributionSaving(true);
            try {
                const updated = await api.updateReportPresentation(projectId, {
                    show_guttenberg_attribution: nextVisible
                });
                setReport((current) => current ? {
                    ...current,
                    manifest: {
                        ...current.manifest,
                        show_guttenberg_attribution: updated.show_guttenberg_attribution
                    }
                } : current);
                setStatus(nextVisible
                    ? "Mention Gutenberg affichee dans la preview et le PDF."
                    : "Mention Gutenberg masquee dans la preview et le PDF.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Impossible de modifier la mention Gutenberg.");
            }
            finally {
                setAttributionSaving(false);
            }
        }

    async function exportFile(kind) {
            setExportRunning(true);
            setStatus(`Export ${kind.toUpperCase()}...`);
            try {
                setLiveSections({});
                await load();
                let result;
                try {
                    result = kind === "pdf" ? await api.exportPdf(projectId) : await api.exportDocx(projectId);
                }
                catch (error) {
                    const detail = error?.detail;
                    if (error?.status !== 409 || !detail?.requires_confirmation || !detail?.can_force_export) {
                        throw error;
                    }
                    const issues = Array.isArray(detail.requires_confirmation_issues) ? detail.requires_confirmation_issues : [];
                    const acknowledgedIssueIds = issues.map((issue) => issue.id).filter(Boolean);
                    result = kind === "pdf"
                        ? await api.exportPdf(projectId, { force: true, acknowledged_issue_ids: acknowledgedIssueIds })
                        : await api.exportDocx(projectId, { force: true, acknowledged_issue_ids: acknowledgedIssueIds });
                }
                window.open(api.exportUrl(projectId, result.filename), "_blank");
                await load();
                const qualityWarnings = Array.isArray(result.quality_warnings) ? result.quality_warnings : [];
                const warningSummary = qualityWarnings[0]?.message || "Des points de qualite restent a verifier.";
                setStatus(qualityWarnings.length
                    ? `Export ${kind.toUpperCase()} termine avec ${qualityWarnings.length} point(s) signale(s). ${warningSummary}`
                    : `Export ${kind.toUpperCase()} termine`);
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setExportRunning(false);
            }
        }

    function updateTextCommentAnchor() {
            if (!commentMode)
                return;
            const selection = window.getSelection();
            const selectedText = selection?.toString().trim() ?? "";
            if (!selection || !selectedText || selection.rangeCount === 0) {
                setCommentAnchor(null);
                return;
            }
            const range = selection.getRangeAt(0);
            const node = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                ? range.commonAncestorContainer.parentElement
                : range.commonAncestorContainer;
            const sectionElement = node?.closest?.("[data-section-id]");
            const paperElement = node?.closest?.(".paper-preview");
            if (!sectionElement || !paperElement) {
                setCommentAnchor(null);
                return;
            }
            const blockElement = node?.closest?.("[data-markdown-block-index]");
            const blockText = blockElement?.textContent ?? "";
            const offset = blockText.indexOf(selectedText);
            const contextBefore = offset >= 0 ? blockText.slice(Math.max(0, offset - 280), offset) : "";
            const contextAfter = offset >= 0 ? blockText.slice(offset + selectedText.length, offset + selectedText.length + 280) : "";
            const blockIndex = blockElement?.dataset.markdownBlockIndex ? Number(blockElement.dataset.markdownBlockIndex) : null;
            const rangeRect = range.getBoundingClientRect();
            const paperRect = paperElement.getBoundingClientRect();
            if (!rangeRect.width && !rangeRect.height) {
                setCommentAnchor(null);
                return;
            }
            setCommentAnchor({
                sectionId: sectionElement.dataset.sectionId ?? "",
                targetType: "text",
                selectedText,
                contextBefore,
                contextAfter,
                blockIndex: Number.isFinite(blockIndex) ? blockIndex : null,
                top: Math.max(88, rangeRect.top - 4),
                left: Math.min(window.innerWidth - 58, paperRect.right + 10)
            });
        }

    function startAnchoredComment() {
            if (!commentAnchor)
                return;
            setPendingComment({
                sectionId: commentAnchor.sectionId,
                targetType: commentAnchor.targetType,
                selectedText: commentAnchor.selectedText,
                contextBefore: commentAnchor.contextBefore,
                contextAfter: commentAnchor.contextAfter,
                blockIndex: commentAnchor.blockIndex,
                imagePath: commentAnchor.imagePath,
                top: Math.min(Math.max(82, commentAnchor.top), window.innerHeight - 250),
                left: Math.min(Math.max(16, commentAnchor.left), window.innerWidth - 380)
            });
            setCommentAnchor(null);
            setCommentDraft("");
            resetManualImageDraft();
        }

    function selectImageForComment(sectionId, imagePath, rect, blockIndex) {
            if (!commentMode)
                return;
            setCommentAnchor({
                sectionId,
                targetType: "image",
                selectedText: "",
                imagePath,
                blockIndex: Number.isFinite(blockIndex) ? blockIndex : null,
                top: Math.max(88, rect.top + 8),
                left: Math.min(window.innerWidth - 58, rect.right + 12)
            });
        }

    function selectImageForEdit(sectionId, imagePath, rect, blockIndex) {
            if (!commentMode)
                return;
            setPendingComment({
                sectionId,
                targetType: "image_edit",
                selectedText: "",
                imagePath,
                blockIndex: Number.isFinite(blockIndex) ? blockIndex : null,
                top: Math.min(Math.max(82, rect.top + 8), window.innerHeight - 250),
                left: Math.min(Math.max(16, rect.right + 12), window.innerWidth - 380)
            });
            setCommentAnchor(null);
            setCommentDraft("");
            resetManualImageDraft();
        }

    function selectChartForComment(sectionId, block, rect, blockIndex) {
            if (!commentMode && !editMode)
                return;
            setPendingComment({
                sectionId,
                targetType: "chart",
                selectedText: chartCommentTargetText(block),
                contextBefore: chartTitle(block.spec),
                contextAfter: "",
                blockIndex: Number.isFinite(blockIndex) ? blockIndex : null,
                top: Math.min(Math.max(82, rect.top + 8), window.innerHeight - 250),
                left: Math.min(Math.max(16, rect.right + 12), window.innerWidth - 380)
            });
            setCommentAnchor(null);
            setCommentDraft("");
            resetManualImageDraft();
        }

    function startInsertionComment(sectionId, blockIndex, blockText, rect) {
            if (!commentMode)
                return;
            setPendingComment({
                sectionId,
                targetType: "insertion",
                selectedText: blockText.slice(0, 900),
                contextBefore: blockText.slice(0, 500),
                contextAfter: "",
                blockIndex,
                top: Math.min(Math.max(82, rect.top), window.innerHeight - 250),
                left: Math.min(Math.max(16, rect.left + 44), window.innerWidth - 380)
            });
            setCommentAnchor(null);
            setCommentDraft("");
            resetManualImageDraft();
        }

    async function submitComment(event) {
            event.preventDefault();
            if (!pendingComment || !commentDraft.trim())
                return;
            await run("Ajout du commentaire...", async () => {
                const attachmentIds = await uploadCommentAttachments();
                await api.createComment(projectId, {
                    section_id: pendingComment.sectionId,
                    target_type: pendingComment.targetType,
                    selected_text: pendingComment.selectedText,
                    context_before: pendingComment.contextBefore,
                    context_after: pendingComment.contextAfter,
                    block_index: pendingComment.blockIndex,
                    image_path: pendingComment.imagePath,
                    comment: commentDraft.trim(),
                    attachment_ids: attachmentIds,
                    branch_id: activeBranchIdRef.current
                });
                setPendingComment(null);
                setCommentAnchor(null);
                setCommentDraft("");
                resetManualImageDraft();
            });
        }

    async function uploadCommentAttachments() {
            if (!manualImageFile)
                return [];
            const uploaded = await api.uploadContextImage(projectId, manualImageFile, manualImageCaption.trim() || defaultImageCaption(manualImageFile), manualImageNote.trim(), activeBranchIdRef.current);
            return [uploaded.attachment.id];
        }

    async function insertManualImageFromComposer() {
            if (!pendingComment || pendingComment.targetType !== "insertion" || !manualImageFile)
                return;
            const sectionId = pendingComment.sectionId;
            const imageKey = imageProcessingKey(sectionId, `insert:${pendingComment.blockIndex ?? "end"}`);
            setImageProcessingKey(imageKey, true);
            setSectionSaving(sectionId, true);
            setStatus("Insertion de l'image...");
            try {
                const updatedManifest = await api.insertSectionImage(projectId, pendingComment.sectionId, manualImageFile, manualImageCaption.trim() || defaultImageCaption(manualImageFile), pendingComment.blockIndex, manualImageNote.trim(), "", activeBranchIdRef.current);
                setPendingComment(null);
                setCommentAnchor(null);
                setCommentDraft("");
                resetManualImageDraft();
                await refreshReportAfterImageChange(sectionId, updatedManifest, activeBranchIdRef.current);
                setStatus("Image inseree dans le rapport.");
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
            }
            finally {
                setImageProcessingKey(imageKey, false);
                setSectionSaving(sectionId, false);
            }
        }

    async function submitAndTreatComment() {
            if (!pendingComment || !commentDraft.trim())
                return;
            const isImageEdit = pendingComment.targetType === "image_edit" && pendingComment.imagePath;
            const imageKey = isImageEdit ? imageProcessingKey(pendingComment.sectionId, pendingComment.imagePath) : "";
            if (isImageEdit)
                setImageProcessingKey(imageKey, true);
            else
                setBusy(true);
            setStatus("Preparation de la suggestion IA...");
            try {
                const attachmentIds = await uploadCommentAttachments();
                const commentText = commentDraft.trim();
                const manifest = await api.createComment(projectId, {
                    section_id: pendingComment.sectionId,
                    target_type: pendingComment.targetType,
                    selected_text: pendingComment.selectedText,
                    context_before: pendingComment.contextBefore,
                    context_after: pendingComment.contextAfter,
                    block_index: pendingComment.blockIndex,
                    image_path: pendingComment.imagePath,
                    comment: commentText,
                    attachment_ids: attachmentIds,
                    branch_id: activeBranchIdRef.current
                });
                const created = [...manifest.comments].reverse().find((comment) => comment.status === "open" &&
                    comment.section_id === pendingComment.sectionId &&
                    comment.comment === commentText &&
                    (!isImageEdit || comment.image_path === pendingComment.imagePath));
                setPendingComment(null);
                setCommentAnchor(null);
                setCommentDraft("");
                resetManualImageDraft();
                if (!created)
                    throw new Error("Commentaire cree mais introuvable.");
                setProcessingCommentIds((current) => ({ ...current, [created.id]: true }));
                if (isImageEdit)
                    rememberImageComment(created.id, created.section_id, created.image_path);
                const createdJob = await api.suggestComment(projectId, created.id, activeBranchIdRef.current);
                applyJobUpdate(createdJob);
                await load();
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                if (isImageEdit)
                    setImageProcessingKey(imageKey, false);
                else
                    setBusy(false);
            }
        }

    async function autoRetouchImage(sectionId, imagePath, blockIndex) {
            if (!sectionId || !imagePath)
                return;
            const imageKey = imageProcessingKey(sectionId, imagePath);
            setImageProcessingKey(imageKey, true);
            setStatus("Retouche automatique de l'image...");
            try {
                const manifest = await api.createComment(projectId, {
                    section_id: sectionId,
                    target_type: "image_edit",
                    selected_text: "",
                    context_before: "",
                    context_after: "",
                    block_index: Number.isFinite(blockIndex) ? blockIndex : null,
                    image_path: imagePath,
                    comment: AUTO_RETOUCH_INSTRUCTION,
                    attachment_ids: [],
                    branch_id: activeBranchIdRef.current
                });
                const created = [...manifest.comments].reverse().find((comment) => comment.status === "open" &&
                    comment.section_id === sectionId &&
                    comment.image_path === imagePath &&
                    comment.comment === AUTO_RETOUCH_INSTRUCTION);
                if (!created)
                    throw new Error("Commentaire de retouche cree mais introuvable.");
                setProcessingCommentIds((current) => ({ ...current, [created.id]: true }));
                rememberImageComment(created.id, created.section_id, created.image_path);
                const createdJob = await api.suggestComment(projectId, created.id, activeBranchIdRef.current);
                applyJobUpdate(createdJob);
                await load();
            }
            catch (error) {
                setStatus(error instanceof Error ? error.message : "Erreur");
                setImageProcessingKey(imageKey, false);
            }
        }

    async function saveAnalysis() {
            await run("Enregistrement de l'analyse...", async () => {
                const saved = await api.updateAnalysis(projectId, analysisDraft);
                analysisDraftDirtyRef.current = false;
                applyAnalysisUpdate(saved, { forceDraft: true });
            });
        }

    async function updateVisualAssetStatus(assetId, status, options = {}) {
            if (visualActionsLocked()) {
                setStatus("Images verrouillees pendant l'analyse du dossier.");
                return;
            }
            const scope = ["source", "all"].includes(options.scope) ? options.scope : "asset";
            const targetAsset = analysis?.visual_assets?.find((asset) => asset.id === assetId);
            const sourceKey = visualAssetSourceKey(targetAsset);
            const previousAssets = scope === "all"
                ? (analysis?.visual_assets ?? []).filter((asset) => asset.type !== "chart")
                : scope === "source" && sourceKey
                ? (analysis?.visual_assets ?? []).filter((asset) => asset.type !== "chart" && visualAssetSourceKey(asset) === sourceKey)
                : targetAsset ? [targetAsset] : [];
            const affectedIds = new Set(previousAssets.map((asset) => asset.id));
            const editorialStatus = status === "accepted" ? "selected" : status;
            patchVisualAssets(affectedIds.size ? affectedIds : [assetId], { status, editorial_status: editorialStatus });
            try {
                const saved = await api.updateVisualAsset(projectId, assetId, { status, scope });
                if (saved.assets?.length)
                    saved.assets.forEach((asset) => patchVisualAsset(asset.id, asset));
                else if (saved.asset)
                    patchVisualAsset(assetId, saved.asset);
                else
                    applyAnalysisUpdate(saved);
                applyLightAnalysisState(saved.analysis_state);
                if (scope === "all")
                    setStatus(`${saved.assets?.length || previousAssets.length || 1} image(s) écartée(s) par défaut. Sélectionne maintenant celles à conserver.`);
                else if (scope === "source")
                    setStatus(`${saved.assets?.length || previousAssets.length || 1} image(s) du document écartée(s).`);
            }
            catch (error) {
                previousAssets.forEach((asset) => patchVisualAsset(asset.id, asset));
                setStatus(error instanceof Error ? error.message : "Impossible d'enregistrer le choix d'image.");
                throw error;
            }
        }

    async function updateVisualAssetLabel(assetId, userLabel) {
            if (visualActionsLocked()) {
                setStatus("Note non enregistree: l'analyse du dossier est en cours.");
                return;
            }
            const previousAsset = analysis?.visual_assets?.find((asset) => asset.id === assetId);
            patchVisualAsset(assetId, { user_label: userLabel });
            try {
                const saved = await api.updateVisualAsset(projectId, assetId, { user_label: userLabel });
                if (saved.asset)
                    patchVisualAsset(assetId, saved.asset);
                else
                    applyAnalysisUpdate(saved);
                applyLightAnalysisState(saved.analysis_state);
            }
            catch (error) {
                if (previousAsset)
                    patchVisualAsset(assetId, previousAsset);
                setStatus(error instanceof Error ? error.message : "Impossible d'enregistrer la note d'image.");
                throw error;
            }
        }

    function visualActionsLocked() {
            return analysisRunning || project?.intake_status === "running";
        }

    function applyJobUpdate(nextJob) {
            setJob(nextJob);
            if (nextJob?.result?.trace) {
                setJobTrace((current) => mergeJobTrace(current, nextJob.result.trace));
            }
            syncJobUiState(nextJob);
        }

    function syncJobUiState(nextJob) {
            if (!nextJob)
                return;
            const active = isActiveJob(nextJob);
            if (!active) {
                if (["prepare-analysis", "prepare-visuals"].includes(nextJob.type))
                    setAnalysisRunning(false);
                if (nextJob.type === "generate-outline") {
                    setOutlineRunning(false);
                    setReportLivePhase("idle");
                }
            }
            if (nextJob.type === "prepare-analysis") {
                setAnalysisRunning(active);
                if (active) {
                    setMainTab("analysis");
                    setStatus(nextJob.message || "Analyse du dossier en cours.");
                }
            }
            if (nextJob.type === "prepare-visuals" && active) {
                setMainTab("analysis");
                setStatus(nextJob.message || "Extraction des images en cours.");
            }
            if (nextJob.type === "generate-outline") {
                setOutlineRunning(active);
                setReportLivePhase(active ? "planning" : "idle");
                if (active) {
                    setMainTab("report");
                    setStatus(nextJob.message || "Generation du plan en cours.");
                }
            }
            if (nextJob.type === "drive-import" && active) {
                setStatus(nextJob.message || "Import Drive en cours.");
            }
            if (nextJob.type === "integrate-context" && active) {
                setStatus(nextJob.message || "Integration des nouveaux inputs en cours.");
            }
        }

    function isActiveJob(nextJob) {
            return nextJob?.status === "queued" || nextJob?.status === "running";
        }

    function operationLocked() {
            return operationStartingRef.current || busy || analysisRunning || isActiveJob(job) || project?.intake_status === "running";
        }

    return {
        activeAssistantReview,
        activeBranchId,
        activeBranchIdRef,
        activeClarificationQuestion,
        activeReportAssistantThreadId,
        activeReportTool,
        activeVersion,
        activityClock,
        aiMode,
        aiProvider,
        aiProviderBusy,
        aiProviderInfo,
        analysis,
        analysisDraft,
        analysisDraftDirtyRef,
        analysisRunning,
        analyzeProject,
        resetAnalysis,
        answerClarificationQuestion,
        appliedReportAssistantActions,
        applyAnalysisUpdate,
        applyJobUpdate,
        applyLightProjectState,
        applyProjectUpdate,
        approveOutline,
        attributionSaving,
        autoRetouchImage,
        branchName,
        branchState,
        browserNotificationPermission,
        busy,
        changeAiMode,
        changeAiProvider,
        chatMessage,
        clarificationAnswerBusy,
        clarificationQuestions,
        clearDriveSearch,
        clearImageProcessingForComment,
        clearLiveSectionsForJob,
        collaboration,
        collaborationContextRef,
        collaborationSessionRef,
        commentAnchor,
        commentDraft,
        commentMode,
        commentReplies,
        contextAutoIntegrationRef,
        contextDraft,
        contextPanelOpen,
        contextSaving,
        contextUploadBusy,
        costs,
        driveBreadcrumbs,
        driveFiles,
        driveLoading,
        driveQuery,
        driveSelection,
        editMode,
        editingTargetRef,
        enableBrowserQuestionNotifications,
        exportFile,
        exportRunning,
        fastMode,
        fastModeBusy,
        fastModeInfo,
        generateOutline,
        imageEditTarget,
        importSelectedDriveFiles,
        initialTabResolved,
        initialTabResolvedRef,
        insertManualImageFromComposer,
        intakeEvents,
        isActiveJob,
        job,
        jobTrace,
        liveSections,
        load,
        mainTab,
        manualImageCaption,
        manualImageFile,
        manualImageNote,
        mergeFeedback,
        milestoneLabel,
        notifiedQuestionIdsRef,
        openDriveBreadcrumb,
        openDriveFolder,
        operationLocked,
        operationStartingRef,
        outlineApprovalPending,
        outlineDraft,
        outlineInstruction,
        outlinePreview,
        outlineRunning,
        pendingComment,
        prepareVisuals,
        processingCommentIds,
        processingImageKeys,
        processingSectionIds,
        project,
        refineOutline,
        refreshDriveFiles,
        refreshReportAfterImageChange,
        rememberImageComment,
        report,
        reportLoaded,
        reportAssistantThinking,
        reportInstruction,
        reportInstructionBankImages,
        reportInstructionFiles,
        reportInstructionImages,
        reportLivePhase,
        reportLivePhaseRef,
        reportOutlineCollapsed,
        resetManualImageDraft,
        run,
        runFinalReview,
        saveAnalysis,
        saveContextPanel,
        savingSectionIds,
        searchDriveFiles,
        selectChartForComment,
        selectImageForComment,
        selectImageForEdit,
        selectedBankImage,
        sendChat,
        setActiveAssistantReview,
        setActiveBranchForSession,
        setActiveClarificationQuestion,
        setActiveReportAssistantThreadId,
        setActiveReportTool,
        setActiveVersion,
        setActivityClock,
        setAnalysis,
        setAnalysisRunning,
        setAppliedReportAssistantActions,
        setBranchName,
        setBranchState,
        setBusy,
        setChatMessage,
        setClarificationQuestions,
        setCollaboration,
        setCommentAnchor,
        setCommentDraft,
        setCommentMode,
        setCommentReplies,
        setContextPanelOpen,
        setDriveQuery,
        setEditMode,
        setImageEditTarget,
        setImageProcessingKey,
        setInitialTabResolved,
        setIntakeEvents,
        setJobTrace,
        setLiveSections,
        setMainTab,
        setManualImageCaption,
        setManualImageFile,
        setManualImageNote,
        setMergeFeedback,
        setMilestoneLabel,
        setOutlineDraft,
        setOutlineInstruction,
        setOutlinePreview,
        setOutlineRunning,
        setPendingComment,
        setProcessingCommentIds,
        setProcessingSectionIds,
        setProject,
        setReportAssistantThinking,
        setReportInstruction,
        setReportInstructionBankImages,
        setReportInstructionFiles,
        setReportInstructionImages,
        setReportLivePhase,
        setReportOutlineCollapsed,
        setSavingSectionIds,
        setSectionSaving,
        setSelectedBankImage,
        setStatus,
        setWorkflowCollapsed,
        startAnchoredComment,
        startGeneration,
        startInsertionComment,
        stateReloadTimerRef,
        status,
        submitAndTreatComment,
        submitComment,
        submitOutlineInstruction,
        syncBrief,
        targetPages,
        titleDraft,
        toggleDriveFile,
        toggleGuttenbergAttribution,
        updateAnalysisDraft,
        updateContextDraft,
        updateTargetPagesDraft,
        updateTextCommentAnchor,
        updateTitleDraft,
        updateVisualAssetLabel,
        updateVisualAssetStatus,
        uploadClientLogo,
        uploadContextAttachments,
        uploadFiles,
        uploadLiveContextFiles,
        uploadReportAssistantFiles,
        workflowCollapsed,
    };
}
